import { SaveState } from '@monorepo/shared/types/SaveState';
import { UTCEquivalentOfLocal } from 'mapistry-shared';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  fetchCalendarEvents,
  updateCalendarAssignmentsAction,
  resetCalendarErrorStateAction,
} from '../../../actions/calendar';
import APP from '../../../config';
import { delayedModalClose } from '../../../utils';
import { CalendarEventType, InspectionAssignTaskType } from '../../propTypes';
import withProvider from '../../withProvider';
import { InspectionAssignModal } from './InspectionAssignModal';

const defaultErrorMessage =
  'There was a problem updating the inspection. Please try again later.';

// Isolated Redux key so month navigation in this modal does not overwrite the
// calendar widget's shared events slice (which drives frequency detection).
const getAssignModalWidgetId = (calendarName) => `assign-${calendarName}`;

class BaseInspectionAssignModalContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Seed a single empty row so the "Select a user" field is visible on
      // open. Blank rows are ignored, so the assignees are unchanged until the
      // user picks someone or removes a current assignee.
      assignees: [null],
      removedUserIds: [],
      saveState: SaveState.CLEAN,
      startDate: this.getTodayUTC(),
    };
  }

  componentDidMount() {
    const { fetchEvents, projectId, task } = this.props;
    const { startDate } = this.state;
    fetchEvents(projectId, task.calendarName, new Date(startDate));
  }

  componentDidUpdate(prevProps) {
    const { isSaving, serverError } = this.props;
    const { saveState } = this.state;

    if (saveState === SaveState.SAVING && prevProps.isSaving && !isSaving) {
      if (serverError) {
        this.setState({ saveState: SaveState.DIRTY });
      } else {
        this.setState({ saveState: SaveState.SAVED }, () => {
          delayedModalClose(this.handleOnClose);
        });
      }
    }
  }

  getTodayUTC() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return UTCEquivalentOfLocal(today);
  }

  // Widen to the matching period so a mid-period date reassigns the whole interval
  getContainingPeriod(startDate) {
    const { events } = this.props;
    if (!startDate || !events?.length) return null;

    const target = new Date(startDate).getTime();
    return (
      events.find(
        (event) =>
          event.startDate &&
          event.dueDate &&
          new Date(event.startDate).getTime() <= target &&
          target <= new Date(event.dueDate).getTime(),
      ) ?? null
    );
  }

  getSaveInterval(forAllFutureTasks) {
    const { startDate } = this.state;
    const period = this.getContainingPeriod(startDate);

    return {
      start: period?.startDate ?? startDate,
      end: forAllFutureTasks ? null : period?.dueDate ?? null,
    };
  }

  // Who is actually on the hook for the chosen date. We evaluate each loaded
  // assignment's own date range (rather than a single period) so that changing
  // the start date into a future inspection reflects that period's assignee,
  // and open-ended ("all future") assignments are still picked up.
  getAssigneesForDate(date) {
    const { events } = this.props;
    if (!date || !events?.length) return [];

    const target = new Date(date).getTime();
    const seen = new Set();
    return events
      .flatMap((event) => event.assignees ?? [])
      .filter((assignee) => {
        const key = assignee.assignmentId ?? assignee.userId;
        if (seen.has(key)) return false;
        seen.add(key);

        const start = assignee.startDate
          ? new Date(assignee.startDate).getTime()
          : Number.NEGATIVE_INFINITY;
        const end = assignee.endDate
          ? new Date(assignee.endDate).getTime()
          : Number.POSITIVE_INFINITY;
        return start <= target && target <= end;
      });
  }

  // Who is on call for the currently selected date, preferring the exact
  // inspection period when the date falls inside one.
  getCurrentlyAssigned() {
    const { startDate } = this.state;
    const period = this.getContainingPeriod(startDate);
    return period?.assignees ?? this.getAssigneesForDate(startDate);
  }

  // Current assignees the user has not removed from the chip list.
  getKeptAssignees() {
    const { removedUserIds } = this.state;
    return this.getCurrentlyAssigned().filter(
      (user) => !removedUserIds.includes(user.userId),
    );
  }

  getAssigneesToSave() {
    const { assignees } = this.state;
    const byUserId = new Map(
      [...this.getKeptAssignees(), ...assignees.filter(Boolean)].map((user) => [
        user.userId,
        user,
      ]),
    );
    return [...byUserId.values()];
  }

  // A local-month comparison shifts a day for browsers behind UTC, which left
  // the 1st of the month reading the previous month's events.
  hasLoadedEventsFor(date) {
    const { currentInterval } = this.props;
    if (!currentInterval) return false;

    const target = date.getTime();
    return (
      new Date(currentInterval.start).getTime() <= target &&
      target <= new Date(currentInterval.end).getTime()
    );
  }

  handleAssigneesChange = (newAssignees) => {
    this.setState({ assignees: newAssignees });
  };

  handleRemoveAssignee = (userId) => {
    this.setState(({ removedUserIds }) => ({
      removedUserIds: [...removedUserIds, userId],
    }));
  };

  handleStartDateChange = (startDate) => {
    // Allow invalid dates through during editing; only fall back to today when
    // the field is fully cleared (null), not when backspacing into invalid text.
    // Convert the picker's local date to UTC storage format here rather than in
    // the presentational layer.
    const toUTC = (d) =>
      d instanceof Date && !Number.isNaN(d.getTime())
        ? UTCEquivalentOfLocal(d)
        : d;
    const newDate = startDate === null ? this.getTodayUTC() : toUTC(startDate);
    const { fetchEvents, projectId, task } = this.props;

    // Removals apply to the date's assignments, so they don't carry over.
    this.setState({ removedUserIds: [], startDate: newDate });

    // Re-fetch when the date leaves the loaded interval so getContainingPeriod
    // and getAssigneesForDate see the right events. Skip while the user is
    // mid-edit with an invalid date (e.g. backspacing).
    const nextIsValid =
      newDate instanceof Date && !Number.isNaN(newDate.getTime());
    if (nextIsValid && !this.hasLoadedEventsFor(newDate)) {
      fetchEvents(projectId, task.calendarName, newDate);
    }
  };

  handleOnClose = () => {
    const { onClose, resetErrorState } = this.props;

    resetErrorState();
    onClose();
  };

  handleSave = (forAllTasks) => {
    const { projectId, updateAssignments, task } = this.props;
    const { calendarId } = task;
    const interval = this.getSaveInterval(forAllTasks);
    const selectedAssignees = this.getAssigneesToSave();

    this.setState(
      {
        saveState: SaveState.SAVING,
      },
      () => {
        updateAssignments(projectId, calendarId, selectedAssignees, interval);
      },
    );
  };

  // Whether the assignee list differs from who is currently on call
  // Blank new assignee rows are ignored
  hasPendingChanges() {
    const selectionUserIds = this.getAssigneesToSave().map(
      (user) => user.userId,
    );
    const currentUserIds = [
      ...new Set(this.getCurrentlyAssigned().map((user) => user.userId)),
    ];

    if (selectionUserIds.length !== currentUserIds.length) return true;
    const selectionSet = new Set(selectionUserIds);
    return currentUserIds.some((id) => !selectionSet.has(id));
  }

  render() {
    const { isOpen, serverError, task } = this.props;
    const { assignees, saveState, startDate } = this.state;
    const period = this.getContainingPeriod(startDate);

    // While a save is in flight (or just finished) we surface that lifecycle
    // state; otherwise the button is enabled purely based on whether there is
    // anything to save.
    const isSaveLifecycle =
      saveState === SaveState.SAVING || saveState === SaveState.SAVED;
    const pendingSaveState = this.hasPendingChanges()
      ? SaveState.DIRTY
      : SaveState.CLEAN;
    const effectiveSaveState = isSaveLifecycle ? saveState : pendingSaveState;

    return (
      <InspectionAssignModal
        assignees={assignees}
        currentlyAssigned={this.getKeptAssignees()}
        errors={serverError ? [defaultErrorMessage] : null}
        isOpen={isOpen}
        onChange={this.handleAssigneesChange}
        onClose={this.handleOnClose}
        onRemoveAssignee={this.handleRemoveAssignee}
        onSave={this.handleSave}
        onStartDateChange={this.handleStartDateChange}
        periodEnd={period?.dueDate ?? null}
        periodStart={period?.startDate ?? null}
        saveState={effectiveSaveState}
        startDate={startDate}
        task={task}
      />
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { calendar } = state;

  const isUpdatingAssignees = calendar.isFetching?.updateAssignees ?? false;
  const widgetId = getAssignModalWidgetId(ownProps.task.calendarName);

  return {
    currentInterval: calendar.currentIntervals?.[widgetId] ?? null,
    events: calendar.events?.[widgetId] ?? [],
    isSaving: isUpdatingAssignees,
    projectId: APP.projectId,
    serverError: calendar.errorMessage,
  };
};

const mapDispatchToProps = (dispatch) => ({
  fetchEvents: (projectId, calendarName, date) =>
    dispatch(
      fetchCalendarEvents({
        calendarName,
        date,
        projectId,
        widgetId: getAssignModalWidgetId(calendarName),
      }),
    ),
  resetErrorState: () => dispatch(resetCalendarErrorStateAction()),

  updateAssignments: (projectId, calendarId, assignments, interval) =>
    dispatch(
      updateCalendarAssignmentsAction(
        projectId,
        calendarId,
        assignments,
        interval,
      ),
    ),
});

BaseInspectionAssignModalContainer.propTypes = {
  currentInterval: PropTypes.shape({
    start: PropTypes.string,
    end: PropTypes.string,
  }),
  events: PropTypes.arrayOf(CalendarEventType).isRequired,
  fetchEvents: PropTypes.func.isRequired,
  task: InspectionAssignTaskType.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  updateAssignments: PropTypes.func.isRequired,
  resetErrorState: PropTypes.func.isRequired,
  serverError: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

BaseInspectionAssignModalContainer.defaultProps = {
  currentInterval: null,
  serverError: null,
};

const InspectionAssignModalContainer = withProvider(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(BaseInspectionAssignModalContainer),
);
export { InspectionAssignModalContainer };
